import React, { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Typography, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRandomPopup, recordPopupClick, clearPopup } from '../../store/slices/popupSlice';
import { canShowPopup, markPopupShown } from '../../utils/popupDisplay';
import { useNavigate } from 'react-router-dom';

const PopupAdvertisement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentPopup, loading } = useSelector((state) => state.popup);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);

  // Charger un popup à chaque changement de connexion ou au premier affichage
  useEffect(() => {
    dispatch(clearPopup());
    setOpen(false);
    dispatch(fetchRandomPopup(isAuthenticated));
  }, [dispatch, isAuthenticated]);

  // Ouvrir le dialog si l'API a renvoyé un popup et la fréquence l'autorise
  useEffect(() => {
    if (!currentPopup || loading) {
      setOpen(false);
      return;
    }

    if (!canShowPopup(currentPopup)) {
      return;
    }

    const timer = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(timer);
  }, [currentPopup, loading]);

  const handleClose = () => {
    if (currentPopup) {
      markPopupShown(currentPopup);
    }
    setOpen(false);
    dispatch(clearPopup());
  };

  const handleCTAClick = () => {
    if (currentPopup?.id) {
      dispatch(recordPopupClick(currentPopup.id));
    }

    if (currentPopup?.cta_link) {
      if (currentPopup.cta_link.startsWith('http')) {
        window.open(currentPopup.cta_link, '_blank', 'noopener,noreferrer');
      } else {
        navigate(currentPopup.cta_link);
      }
    }

    handleClose();
  };

  if (!currentPopup || !open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '20px', md: '30px' },
          overflow: 'hidden',
          position: 'relative',
          maxWidth: { xs: '90%', sm: '500px' },
          m: 2,
          boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.2)',
          backgroundColor: '#FFF9F0',
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        aria-label="Fermer"
        sx={{
          position: 'absolute',
          top: { xs: 8, sm: 12 },
          right: { xs: 8, sm: 12 },
          zIndex: 10,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
          width: { xs: 32, sm: 40 },
          height: { xs: 32, sm: 40 },
        }}
      >
        <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
      </IconButton>

      {currentPopup.image_url && (
        <Box
          sx={{
            width: '100%',
            height: { xs: '220px', sm: '300px', md: '360px' },
            overflow: 'hidden',
            backgroundColor: '#FFF9F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pt: { xs: 6, sm: 7 },
            pb: { xs: 0.5, sm: 1 },
          }}
        >
          <Box
            component="img"
            src={currentPopup.image_url}
            alt={currentPopup.title}
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          pt: { xs: 0.5, sm: 0.75 },
          pb: { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 2.5 },
          backgroundColor: '#FFF9F0',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            fontFamily: 'Achiko',
            color: '#784e3c',
            mb: 1.5,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
          }}
        >
          {currentPopup.title}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#333',
            mb: 2,
            lineHeight: 1.6,
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {currentPopup.description}
        </Typography>

        {currentPopup.cta_text && (
          <Button
            onClick={handleCTAClick}
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: '#784e3c',
              color: 'white',
              fontWeight: 'bold',
              fontFamily: 'Achiko',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              py: { xs: 1.5, sm: 2 },
              borderRadius: '25px',
              textTransform: 'none',
              boxShadow: '0px 4px 15px rgba(235, 164, 91, 0.4)',
              '&:hover': {
                backgroundColor: '#D89449',
                boxShadow: '0px 6px 20px rgba(235, 164, 91, 0.6)',
              },
            }}
          >
            {currentPopup.cta_text}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default PopupAdvertisement;
